import { BarMouseOverState } from '../BarMouseOver';
import { NumberFormat } from '@instinet-analytics/core-lib';
import { PortfolioSummaryAggregatedData } from 'widgets/portfolioSummary/store/data/PortfolioSummaryDataState';
import { PortfolioSummaryConfigurationState } from 'widgets/portfolioSummary/store/instances/configuration/PortfolioSummaryConfigurationState';
import { CurrencyFormat } from '@instinet-analytics/core-lib';

export interface PortfolioSummaryChartLegendData {
    bar?: 'buy' | 'sell' | 'net' | 'short';
    traded?: string;
    remaining?: string;
    tradedPercent?: string;
    remainingPercent?: string;
    ordered?: string;
}

export function getLegendData(
    numberFormat: NumberFormat,
    currencyFormat: CurrencyFormat,
    config: PortfolioSummaryConfigurationState,
    showBuysAsPositive: boolean,
    data?: PortfolioSummaryAggregatedData,
    state?: BarMouseOverState
): PortfolioSummaryChartLegendData {
    if (!data || !state?.bar || !state?.key || !state?.groupName) {
        return {};
    }

    const { aggregationField } = config;

    // Get the aggregated group (dealing with the fake "sides" group when we are aggregating by side)...
    const groupName = aggregationField === 'side' ? 'sides' : state.groupName;
    const aggregatedDataGroup = data[groupName];
    if (!aggregatedDataGroup) {
        return {};
    }

    // Get the aggregated data by side (finding the required side from the bar)...
    const sideAggregationName =
        state.bar === 'sell' && state.key.startsWith('short')
            ? 'short'
            : state.bar;
    const aggregatedData = aggregatedDataGroup[sideAggregationName];

    if (!aggregatedData) {
        return {};
    }

    const multiplier =
        !showBuysAsPositive && sideAggregationName === 'net' ? -1 : 1;

    return {
        bar: sideAggregationName,
        traded: formatValue(
            numberFormat,
            currencyFormat,
            multiplier,
            aggregatedData.tradedValue
        ),
        tradedPercent: formatPercent(
            numberFormat,
            aggregatedData.tradedValue,
            aggregatedData.orderedValue
        ),
        remaining: formatValue(
            numberFormat,
            currencyFormat,
            multiplier,
            aggregatedData.leavesValue
        ),
        remainingPercent: formatPercent(
            numberFormat,
            aggregatedData.leavesValue,
            aggregatedData.orderedValue
        ),
        ordered: formatValue(
            numberFormat,
            currencyFormat,
            multiplier,
            aggregatedData.orderedValue
        ),
    };
}

function formatValue(
    numberFormat: NumberFormat,
    currencyFormat: CurrencyFormat,
    multiplier: number,
    value?: number
): string {
    if (value === undefined) {
        return '';
    }
    const valueLoc = value * (currencyFormat.exchangeRate ?? 1) * multiplier;
    const sign = valueLoc >= 0 ? '' : '-';
    const displayValue = numberFormat.n0(Math.abs(valueLoc));
    return sign + currencyFormat.prefix + displayValue;
}

function formatPercent(
    numberFormat: NumberFormat,
    value?: number,
    by?: number
) {
    if (value === undefined) {
        return '';
    }
    if (!by) {
        return '';
    }
    const result = (value / by) * 100;
    return numberFormat.n0(result) + '%';
}
